import { useEffect } from 'react';
import { useSoftphone } from '../store/softphone';
import { useAuthStore } from '../store/auth';
import { ensureSocket } from '../lib/socket';
import type { AgentState } from '@marnic/shared';

const STATES: Array<{ value: AgentState; label: string; emoji: string }> = [
  { value: 'available', label: 'Available', emoji: '✅' },
  { value: 'wrap_up', label: 'Wrap-up', emoji: '✏️' },
  { value: 'break', label: 'Break', emoji: '☕' },
  { value: 'lunch', label: 'Lunch', emoji: '🍽' },
  { value: 'training', label: 'Training', emoji: '📚' },
  { value: 'admin_task', label: 'Admin', emoji: '🗂' },
  { value: 'meeting', label: 'Meeting', emoji: '📅' },
  { value: 'offline', label: 'Offline', emoji: '⏻' },
];

export function AgentStatusSelector(): JSX.Element {
  const agentState = useSoftphone((s) => s.agentState);
  const setAgentState = useSoftphone((s) => s.setAgentState);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Hydrate the dropdown from the server-side saved state on (re)connect.
  // Without this, the store defaults to 'offline' on every refresh.
  useEffect(() => {
    if (!accessToken) return;
    const sock = ensureSocket(accessToken);
    const onInitial = (p: { state: AgentState }): void => {
      setAgentState(p.state);
    };
    sock.on('agent_state_initial', onInitial);
    return () => {
      sock.off('agent_state_initial', onInitial);
    };
  }, [accessToken, setAgentState]);

  return (
    <select
      className="input"
      value={agentState}
      onChange={(e) => {
        if (!user || !accessToken) return;
        const next = e.target.value as AgentState;
        setAgentState(next);
        ensureSocket(accessToken).emit('agent_status_changed', {
          agentId: user.id,
          state: next,
          at: new Date().toISOString(),
        });
      }}
    >
      {STATES.map((s) => (
        <option key={s.value} value={s.value}>
          {s.emoji} {s.label}
        </option>
      ))}
    </select>
  );
}
